
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowLeft, AlertCircle } from 'lucide-react';
import { COLORS, USERS_COLLECTION } from '../config/constants';
import { useAppContext } from '../App.jsx';
import TerviLogo from '../components/common/TerviLogo.jsx';
import { doc, getDoc } from 'firebase/firestore';

const LoginScreen = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const navigate = useNavigate();

    const { db, signIn, logOut, showMessage } = useAppContext();

    const handleLogin = async (e) => {
        e.preventDefault();

        if (failedAttempts >= 5) {
            showMessage("Limite de tentativas excedido. Acesso bloqueado temporariamente.", "error");
            return;
        }

        if (!email || !password || !signIn) return;

        setIsLoading(true);
        try {
            // 1. FAZ LOGIN E PEGA O USUÁRIO DIRETAMENTE (Sem Promises instáveis)
            const userCredential = await signIn(email, password);
            const loggedUser = userCredential.user;

            if (loggedUser) {
                // 2. BUSCA STATUS NO BANCO
                const userDoc = await getDoc(doc(db, USERS_COLLECTION, loggedUser.uid));
                const status = userDoc.data()?.accountStatus;

                if (status === 'banned') {
                    await logOut();
                    setIsLoading(false);
                    showMessage("Esta conta foi banida por violar os termos da plataforma.", "error");
                    return;
                }

                if (status === 'suspended') {
                    await logOut();
                    setIsLoading(false);
                    showMessage("Sua conta está suspensa temporariamente. Entre em contato com o suporte.", "warning");
                    return;
                }
            }

            // Sucesso total. O App.jsx fará o redirecionamento.

        } catch (error) {
            setIsLoading(false);
            const newCount = failedAttempts + 1;
            setFailedAttempts(newCount);

            let errorMsg = "E-mail ou senha incorretos.";

            // Mapeamento de erros do Firebase para português amigável
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                errorMsg = `Senha incorreta. Tentativas restantes: ${5 - newCount}`;
            } else if (error.code === 'auth/user-not-found') {
                errorMsg = "Este e-mail não está cadastrado.";
            } else if (error.code === 'auth/too-many-requests') {
                errorMsg = "Bloqueado temporariamente por excesso de tentativas.";
            }

            showMessage(errorMsg, "error");
        }
    };

    const backgroundStyle = {
        background: `linear-gradient(to right, ${COLORS.SUCCESS_GREEN}, ${COLORS.NEUTRAL_GRAY}, ${COLORS.PRIMARY_BLUE})`,
    };

    return (
        <div className="flex flex-col h-screen p-4" style={backgroundStyle}>
            <div className="w-full flex items-center flex-shrink-0">
                <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/40 transition">
                    <ArrowLeft className="w-6 h-6 text-white" />
                </button>
            </div>

            <div className="flex-grow flex flex-col items-center justify-center px-2">
                <div className="w-full max-w-sm">
                    <div className="text-center mb-8">
                        <div className="flex justify-center mb-2"><TerviLogo size={48} /></div>
                        <p className="text-white/90 mt-1 font-black text-[10px] uppercase tracking-widest">Acesso à Conta</p>
                    </div>

                    <div className="bg-white p-8 rounded-[2.5rem] shadow-2xl relative text-left">
                        <form onSubmit={handleLogin} className="space-y-5">
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input id="email-login" name="email" type="email" autoComplete="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all text-sm font-semibold" required />
                            </div>

                            <div className="space-y-2">
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input id="password-login" name="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full pl-12 pr-12 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-blue-500 focus:bg-white outline-none transition-all text-sm font-semibold" required />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 rounded-full transition-colors">
                                        {showPassword ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
                                    </button>
                                </div>

                                {failedAttempts > 0 && (
                                    <div className={`flex items-center px-3 py-2 rounded-xl transition-all animate-fade-in ${failedAttempts >= 4 ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                                        <AlertCircle size={14} className="flex-shrink-0" />
                                        <span className="text-[10px] font-bold uppercase tracking-tight ml-2">
                                            {failedAttempts >= 5 ? 'Acesso Bloqueado' : `Atenção: Você tem mais ${5 - failedAttempts} ${failedAttempts === 4 ? 'tentativa' : 'tentativas'}`}
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={isLoading || failedAttempts >= 5}
                                    className={`w-full py-4 rounded-2xl font-black text-white shadow-xl transition-all active:scale-[0.98] uppercase tracking-widest text-sm ${failedAttempts >= 5 ? 'bg-gray-300' : 'hover:opacity-90'}`}
                                    style={failedAttempts < 5 ? {backgroundColor: COLORS.ACTION_BLUE} : {}}
                                >
                                    {isLoading ? 'Autenticando...' : failedAttempts >= 5 ? 'Acesso Bloqueado' : 'Entrar'}
                                </button>
                            </div>
                        </form>

                        <div className="text-center mt-8">
                            <a href="#" className="text-[10px] font-black text-blue-600 hover:text-blue-800 transition-colors uppercase tracking-widest">Esqueceu sua senha?</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginScreen;
